import {
    isoAndUnixZodSchema,
    nowIsoAndUnix,
    type IsoAndUnixTimestamp,
} from "@@/shared/date-time/iso-and-unix";
import { timeZodSchema, type Time } from "@@/shared/date-time/time";
import { ownerIdZodSchema, type OwnerId } from "@@/shared/owner-id";
import { percentageZodSchema, type Percentage } from "@@/shared/percentage";
import { priceZodSchema, type Price } from "@@/shared/prices/price";
import {
    translatableZodSchema,
    type Translatable,
} from "@@/shared/translations/translation";
import type { Except } from "type-fest";
import { z } from "zod";
import { feeIdFactory, feeIdZodSchema, type FeeId } from "./fee-id";
import { feeSourceZodSchema, type FeeSource } from "./fee-sources/fee-source";
import { feeTargetZodSchema, type FeeTarget } from "./fee-target";

type _FeeBase = {
    _id: FeeId;
    _type: "FEE";
    _createdAt: IsoAndUnixTimestamp;
    ownerId: OwnerId;
    title: Translatable;
    description: Translatable;
    feeTarget: FeeTarget;
    validTrough: Time;
};
const feeBaseZodSchema: z.ZodSchema<_FeeBase> = z.object({
    _id: feeIdZodSchema,
    _type: z.literal("FEE"),
    _createdAt: isoAndUnixZodSchema,
    ownerId: ownerIdZodSchema,
    title: translatableZodSchema,
    description: translatableZodSchema,

    feeTarget: feeTargetZodSchema,
    validTrough: timeZodSchema,
});

// Percentage fee
type GeneralFee = _FeeBase & {
    type: "GENERAL_FEE";

    percentage: {
        percentage: Percentage;

        /**
         *Should Fee be use incl or escl vatAmount
         *
         * @type {boolean}
         */
        includeVatAmount: boolean;
    };

    /**
     * If fee is lower then minValue, use minValue
     *
     * @type {Price}
     */
    minValue?: Price;
    /**
     * Added flatFee on fee
     *
     * @type {Price}
     */
    flatFee: Price;

    feeSource: FeeSource;
};

const generalFeeZodSchema: z.ZodSchema<GeneralFee> = z.intersection(
    feeBaseZodSchema,
    z.object({
        type: z.literal("GENERAL_FEE"),
        percentage: z.object({
            percentage: percentageZodSchema,
            percentageTarget: z.enum(["TOTAL", "PAYMENT_PROVIDER_FEE"]),
            includeVatAmount: z.boolean(),
        }),

        minValue: priceZodSchema.optional(),
        flatFee: priceZodSchema,
        feeSource: feeSourceZodSchema,
    }),
);

// Value fee not used currently TODO remove
type ValueFee = _FeeBase & {
    type: "VALUE_FEE";
    price: Price;
};

const valueFeeZodSchema: z.ZodSchema<ValueFee> = z.intersection(
    feeBaseZodSchema,
    z.object({
        type: z.literal("VALUE_FEE"),
        price: priceZodSchema,
        feeSource: z.never(),
    }),
);

// Fee
type Fee = GeneralFee | ValueFee;
const feeZodSchema: z.ZodSchema<Fee> = z.union([
    generalFeeZodSchema,
    valueFeeZodSchema,
]);

type generalFeeParams = Except<GeneralFee, "_id" | "_type" | "_createdAt">;
const generalFeeFactory = (params: generalFeeParams) => {
    const command: GeneralFee = {
        ...params,
        _id: feeIdFactory(),
        _type: "FEE",
        _createdAt: nowIsoAndUnix(),
    };
    return command;
};

type FactoryParams = Except<ValueFee, "_id" | "_type" | "_createdAt">;
const valueFeeFactory = (params: FactoryParams) => {
    const command: ValueFee = {
        ...params,
        _id: feeIdFactory(),
        _type: "FEE",
        _createdAt: nowIsoAndUnix(),
    };
    return command;
};

export {
    feeZodSchema,
    generalFeeFactory,
    generalFeeZodSchema,
    valueFeeFactory,
};
export type { Fee, GeneralFee, ValueFee };
